class CartDrawer extends HTMLElement {
  constructor() {
    super();
    console.log('🛍️ CartDrawer constructor called');

    this.isOpen = false;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    const overlay = this.querySelector('#CartDrawer-Overlay');
    console.log('🎯 Cart drawer overlay:', overlay ? 'Found' : 'Not found');
    
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    } else {
      console.warn('⚠️ Cart drawer overlay element not found');
    }
    
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    console.log('🛒 Cart icon bubble:', cartLink ? 'Found' : 'Not found');
    
    if (!cartLink) {
      console.warn('⚠️ Cart icon bubble not found');
      return;
    }
    
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    console.log('🚪 Opening cart drawer', { triggeredBy: triggeredBy?.tagName, isCurrentlyOpen: this.isOpen });
    
    if (this.isOpen) {
      console.log('Cart drawer already open, skipping open animation');
      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    
    // Force iOS Safari to repaint the drawer
    this.style.visibility = 'visible';
    this.style.display = 'flex';
    
    // Force layout repaint before animation for iOS Safari
    void this.offsetHeight;
    
    // Small delay before adding the active class to ensure iOS Safari can process the DOM changes
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.isOpen = true;
      
      // Force repaint after adding active class
      void this.offsetHeight;
      
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        // Force drawer inner to repaint
        void drawerInner.offsetHeight;
      }
    }, 50);

    this.addEventListener('transitionend', () => {
      console.log('✨ Cart drawer animation complete');
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      
      console.log('🎯 Focus elements:', {
        container: containerToTrapFocusOn ? 'Found' : 'Not found',
        focusElement: focusElement ? 'Found' : 'Not found'
      });
      
      if (containerToTrapFocusOn && focusElement) {
        trapFocus(containerToTrapFocusOn, focusElement);
      }
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    console.log('🚪 Closing cart drawer');
    if (!this.isOpen) {
      console.log('Cart drawer already closed, skipping close animation');
      return;
    }

    this.classList.remove('active');
    this.isOpen = false;
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    
    // Hide drawer after animation
    this.addEventListener('transitionend', () => {
      console.log('✨ Close animation complete');
      this.style.visibility = 'hidden';
      this.style.display = 'none';
    }, { once: true });
  }

  renderContents(parsedState) {
    console.log('🔄 Rendering cart drawer contents:', { 
      hasState: !!parsedState,
      hasSections: parsedState?.sections ? 'Yes' : 'No',
      itemCount: parsedState?.item_count,
      sections: Object.keys(parsedState?.sections || {})
    });
    
    // Check if parsedState is valid
    if (!parsedState) {
      console.error('⚠️ Invalid state passed to renderContents - missing parsedState');
      return;
    }
    
    // Update empty state class immediately
    const isEmpty = parsedState.item_count === 0;
    
    // If we have an empty sections array or missing sections object, 
    // we need to fetch the drawer contents directly
    if (!parsedState.sections || Object.keys(parsedState.sections).length === 0) {
      console.log('⚠️ Empty sections object in response. Fetching cart drawer directly.');
      this.fetchCartDrawerContents();
      return;
    }
    
    try {
      // Get the drawer inner element - this is critical to update only the inner content
      const drawerInner = this.querySelector('.drawer__inner');
      console.log('🎯 Drawer inner element:', drawerInner ? 'Found' : 'Not found');
      
      if (!drawerInner) {
        console.error('⚠️ Drawer inner element not found');
        return;
      }

      // Force a repaint before updating content for iOS Safari
      void drawerInner.offsetHeight;
      
      // Handle the is-empty class before DOM updates for iOS Safari
      if (isEmpty) {
        this.classList.add('is-empty');
      } else {
        this.classList.remove('is-empty');
      }

      // Get cart-drawer content from the HTML response
      let updatedContent = '';
      if (parsedState.sections['cart-drawer-content']) {
        updatedContent = parsedState.sections['cart-drawer-content'];
      } else if (parsedState.sections['cart-drawer']) {
        // Extract only the inner content from cart-drawer section
        const parser = new DOMParser();
        const doc = parser.parseFromString(parsedState.sections['cart-drawer'], 'text/html');
        const innerContent = doc.querySelector('.drawer__inner');
        if (innerContent) {
          updatedContent = innerContent.outerHTML;
        } else {
          console.warn('Could not find .drawer__inner in cart-drawer section');
          this.fetchCartDrawerContents();
          return;
        }
      } else {
        console.warn('Cart drawer sections missing in response');
        this.fetchCartDrawerContents();
        return;
      }
      
      // Using setTimeout to ensure DOM operations are separated 
      // from the class changes for iOS Safari
      setTimeout(() => {
        // Replace only the inner content, preserving the outer cart-drawer structure
        const cartDrawerNode = document.getElementById('CartDrawer');
        if (cartDrawerNode) {
          // Clear the old content but maintain the overlay
          const overlay = cartDrawerNode.querySelector('.cart-drawer__overlay');
          
          // Create a temporary div to hold the new content
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = updatedContent;
          
          // Replace the old inner content while preserving the overlay
          cartDrawerNode.innerHTML = '';
          if (overlay) cartDrawerNode.appendChild(overlay);
          
          // Append the new drawer inner content
          while (tempDiv.firstChild) {
            cartDrawerNode.appendChild(tempDiv.firstChild);
          }
          
          // Force repaint after content update for iOS Safari
          void cartDrawerNode.offsetHeight;
        } else {
          console.error('CartDrawer element not found');
          return;
        }
        
        // Update cart icon if available
        if (parsedState.sections['cart-icon-bubble']) {
          const iconContainer = document.querySelector('.shopify-section--cart-icon-bubble');
          if (iconContainer) {
            iconContainer.innerHTML = parsedState.sections['cart-icon-bubble'];
          }
        }
        
        // Initialize any new elements
        this.initializeNewElements(cartDrawerNode);
        
        // Ensure drawer is open
        console.log('🚪 Opening drawer after content update');
        this.open();
        
        // Notify other components
        console.log('📢 Publishing cart update event');
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: parsedState });
      }, 50);
    } catch (error) {
      console.error('❌ Error rendering cart drawer contents:', error);
      this.fetchCartDrawerContents(); // Fallback to direct fetch on any error
    }
  }

  fetchCartDrawerContents() {
    console.log('🔄 Fetching cart drawer contents directly');
    
    fetch(`${routes.cart_url}?section_id=cart-drawer-content`)
      .then(response => response.text())
      .then(responseText => {
        try {
          // Extract the cart drawer content from the HTML
          const parser = new DOMParser();
          const doc = parser.parseFromString(responseText, 'text/html');
          const drawerContentElement = doc.querySelector('.drawer__inner');
          
          if (!drawerContentElement) {
            throw new Error('Could not find drawer inner content in the response');
          }
          
          // Get the content HTML
          const drawerContent = drawerContentElement.outerHTML;
          
          // Get the cart-drawer element
          const cartDrawerNode = document.getElementById('CartDrawer');
          if (!cartDrawerNode) {
            throw new Error('CartDrawer element not found in DOM');
          }
          
          // Preserve the overlay
          const overlay = cartDrawerNode.querySelector('.cart-drawer__overlay');
          
          // Force repaint before content update for iOS Safari
          void cartDrawerNode.offsetHeight;
          
          // Update empty state based on content
          const isEmpty = !responseText.includes('cart-item');
          this.classList.toggle('is-empty', isEmpty);
          
          // Clear existing content but keep the overlay
          cartDrawerNode.innerHTML = '';
          if (overlay) cartDrawerNode.appendChild(overlay);
          
          // Add the new drawer content with a small delay for iOS Safari
          setTimeout(() => {
            // Create a temporary container for the content
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = drawerContent;
            
            // Append all nodes from the temp div to the drawer
            while (tempDiv.firstChild) {
              cartDrawerNode.appendChild(tempDiv.firstChild);
            }
            
            // Force repaint after content update
            void cartDrawerNode.offsetHeight;
            
            // Initialize elements and open the drawer
            this.initializeNewElements(cartDrawerNode);
            this.open();
          }, 50);
        } catch (error) {
          console.error('❌ Error processing cart drawer contents:', error);
          this.fetchCartData(); // Fallback to direct cart data
        }
      })
      .catch(error => {
        console.error('❌ Error fetching cart drawer contents:', error);
        // On error, try fetching just the cart data to at least update the state
        this.fetchCartData();
      });
  }

  fetchCartData() {
    console.log('🔄 Fetching basic cart data as fallback');
    
    fetch(`${routes.cart_url}.js`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`Cart fetch failed with status: ${response.status}`);
        }
        return response.text();  // Change to text() to handle parsing manually
      })
      .then(responseText => {
        let cart;
        try {
          // Try standard JSON parsing first
          cart = JSON.parse(responseText);
        } catch (parseError) {
          console.error('❌ Error parsing cart JSON response:', parseError);
          console.log('Response that failed to parse:', responseText.substring(0, 500) + '...');
          
          // Try to fix common JSON syntax errors
          try {
            // Special handling for the specific error we're getting at position ~8160
            if (parseError.message.includes("position 816")) {
              console.log('Detected the specific 8160 position error, attempting targeted fix...');
              
              // Get a window of characters around the error position
              const startPos = Math.max(0, 8160 - 20);
              const endPos = Math.min(responseText.length, 8160 + 20);
              const problematicSection = responseText.substring(startPos, endPos);
              console.log('Problematic section:', problematicSection);
              
              // Create a fixed version of the JSON string
              let fixedText = responseText;
              
              // Apply specific fixes for position 8160
              // First check if there's a pattern like 'something"something' without a comma
              fixedText = responseText.substring(0, 8160).replace(/(\w+")\s*("[a-zA-Z])/g, '$1,$2');
              fixedText += responseText.substring(8160);
              
              // Also check for trailing commas in arrays or objects
              fixedText = fixedText.replace(/,\s*([}\]])/g, '$1');
              
              // Check for unbalanced brackets and add missing ones
              const openBrackets = (fixedText.match(/\[/g) || []).length;
              const closeBrackets = (fixedText.match(/\]/g) || []).length;
              if (openBrackets > closeBrackets) {
                fixedText += ']'.repeat(openBrackets - closeBrackets);
              }
              
              const openBraces = (fixedText.match(/\{/g) || []).length;
              const closeBraces = (fixedText.match(/\}/g) || []).length;
              if (openBraces > closeBraces) {
                fixedText += '}'.repeat(openBraces - closeBraces);
              }
              
              // As a last resort, try different direct fixes at position 8160
              if (fixedText.length >= 8162) {
                // Try different approaches: insert comma, remove character, etc.
                const attempts = [
                  // Add a comma
                  fixedText.slice(0, 8160) + ',' + fixedText.slice(8160),
                  // Replace character with comma
                  fixedText.slice(0, 8160) + ',' + fixedText.slice(8161),
                  // Remove character
                  fixedText.slice(0, 8160) + fixedText.slice(8161),
                  // Replace with proper array ending
                  fixedText.slice(0, 8160) + ']}' + fixedText.slice(8162)
                ];
                
                // Try each fix attempt in order
                for (const attempt of attempts) {
                  try {
                    console.log('Trying specific repair attempt...');
                    cart = JSON.parse(attempt);
                    console.log('✅ Fix successful!');
                    break; // If successful, exit the loop
                  } catch (e) {
                    console.log('Attempt failed:', e.message);
                    // Continue to next attempt
                  }
                }
                
                // If we have a parsed cart, we can continue
                if (!cart) {
                  // Last resort: If we can't fix it, create a minimal valid state
                  console.log('All repair attempts failed, creating minimal cart object');
                  cart = { item_count: 0, items: [] };
                }
              } else {
                throw new Error("JSON string too short to apply position-specific fix");
              }
            } else if (parseError.message.includes("Expected ',' or ']'") || 
                parseError.message.includes("Expected ',' or '}'")) {
              // Original fix logic for other cases
              let sanitizedText = responseText;
              
              // Remove any trailing commas in arrays or objects
              sanitizedText = sanitizedText.replace(/,\s*([}\]])/g, '$1');
              
              // Add missing closing brackets/braces if needed
              const openBraces = (sanitizedText.match(/\{/g) || []).length;
              const closeBraces = (sanitizedText.match(/\}/g) || []).length;
              if (openBraces > closeBraces) {
                sanitizedText += '}'.repeat(openBraces - closeBraces);
              }
              
              const openBrackets = (sanitizedText.match(/\[/g) || []).length;
              const closeBrackets = (sanitizedText.match(/\]/g) || []).length;
              if (openBrackets > closeBrackets) {
                sanitizedText += ']'.repeat(openBrackets - closeBrackets);
              }
              
              // Try parsing again with the fixed JSON
              console.log('Attempting to parse sanitized JSON');
              cart = JSON.parse(sanitizedText);
              console.log('✅ Successfully parsed sanitized JSON');
            } else {
              throw parseError;
            }
          } catch (secondError) {
            console.error('Failed to fix JSON parsing issue:', secondError);
            // Create minimal cart object to continue operation
            cart = { item_count: 0, items: [] };
          }
        }
        
        // Ensure we have a valid cart object
        if (!cart || typeof cart !== 'object') {
          console.error('Invalid cart object, using empty cart');
          cart = { item_count: 0, items: [] };
        }
        
        console.log('✅ Got cart data:', cart);
        // Update empty state
        const isEmpty = !cart.item_count;
        this.classList.toggle('is-empty', isEmpty);
        
        // At minimum, update the cart count
        const cartCount = document.getElementById('cart-icon-bubble');
        if (cartCount) {
          if (cart.item_count > 0) {
            cartCount.classList.add('has-items');
            const countBubble = cartCount.querySelector('.cart-count-bubble');
            if (countBubble) {
              const countText = countBubble.querySelector('span[aria-hidden="true"]');
              if (countText) {
                countText.textContent = cart.item_count;
              }
            }
          } else {
            // If cart is empty, ensure we show empty state
            cartCount.classList.remove('has-items');
            // Close the drawer and reload the page if we're in a broken state
            if (this.isOpen && document.querySelector('.cart-item') && isEmpty) {
              console.log('Cart is empty but items are showing in the UI. Refreshing drawer.');
              this.classList.add('is-empty');
              
              // Create a simple state object for rendering empty cart
              const emptyState = {
                item_count: 0,
                items: [],
                sections: {}
              };
              
              // Try to render empty cart state
              try {
                this.renderContents(emptyState);
              } catch (error) {
                console.error('Error rendering empty cart:', error);
                // If that fails, just reset the cart drawer by closing and reopening
                this.close();
                setTimeout(() => {
                  window.location.reload();
                }, 500);
              }
            }
          }
        }
        
        // Try one more time to fetch the drawer content
        setTimeout(() => this.fetchCartDrawerContents(), 500);
      })
      .catch(error => {
        console.error('❌ Error fetching cart data:', error);
        
        // On critical error, just close the drawer and force refresh
        if (this.isOpen) {
          console.log('Critical cart error, closing drawer');
          this.close();
          
          // If the user is removing an item and we get an error,
          // it likely means the cart was successfully updated but the response was malformed
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      });
  }

  initializeNewElements(container) {
    console.log('🔄 Initializing new elements');
    
    // Add quantity change handlers
    const quantityInputs = container.querySelectorAll('.quantity__input');
    console.log('Found quantity inputs:', quantityInputs.length);
    
    quantityInputs.forEach(input => {
      input.addEventListener('change', (event) => {
        console.log('Quantity changed:', {
          index: event.target.dataset.index,
          value: event.target.value
        });
        
        const cartItems = this.closest('cart-drawer-items');
        if (cartItems) {
          cartItems.updateQuantity(
            event.target.dataset.index,
            event.target.value,
            document.activeElement.getAttribute('name')
          );
        }
      });
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    try {
      console.log('🔍 Getting section inner HTML for selector:', selector);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const element = tempDiv.querySelector(selector);
      const content = element ? element.innerHTML : '';
      console.log('📝 Section content length:', content.length);
      return content;
    } catch (error) {
      console.error('❌ Error parsing section HTML:', error);
      return '';
    }
  }

  setActiveElement(element) {
    console.log('🎯 Setting active element:', element?.tagName);
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
